import apiConfig from '../apiConfig';
import { getAccessToken } from '../../utils/jwt';
import { getAxiosInstance } from '../../utils/axiosApi';
import { getFilenameFromHttpResponse, postHandler } from '../../utils/apiUtils';
import { Divisions } from '../../utils/constants/Divisions.Constants';
// import axios from 'axios';

const axiosInstance = getAxiosInstance({
  baseUrl: apiConfig.PortalAPIUrl,
});

axiosInstance.interceptors.request.use((config) => {
  config.headers = {
    Authorization: `Bearer ${getAccessToken()}`,
  };

  return config;
});

const searchReports = async ({
  reportName,
  jobName,
  clientSampleNo,
  dateGeneratedFrom,
  dateGeneratedTo,
  reportType,
  locationIds,
  clientIds,
  sourceSystemIDs,
  projects,
  belongsTo,
  sampleNumber,
  gradeId,
  contractIds,
  divisionIds,
  page,
  pageSize,
  sortBy,
}) => {
  try {
    const queryParams = [];

    if (reportName && reportName.trim() !== '') {
      queryParams.push(`reportName=${encodeURIComponent(reportName.trim())}`);
      queryParams.push('reportNameOP=Contains');
    }

    if (jobName && jobName.trim() !== '') {
      queryParams.push(`jobName=${encodeURIComponent(jobName.trim())}`);
      queryParams.push('jobNameOP=Contains');
    }

    if (clientSampleNo && clientSampleNo.trim() !== '') {
      queryParams.push(`clientSampleNo=${encodeURIComponent(clientSampleNo.trim())}`);
      queryParams.push('clientSampleNoOP=Contains');
    }

    if (dateGeneratedFrom) {
      queryParams.push(
        `dateGeneratedFrom=${encodeURIComponent(dateGeneratedFrom)}`
      );
    }

    if (dateGeneratedTo) {
      queryParams.push(
        `dateGeneratedTo=${encodeURIComponent(dateGeneratedTo)}`
      );
    }

    if (reportType) {
      queryParams.push(`reportType=${encodeURIComponent(reportType)}`);
    }

    if (Array.isArray(locationIds) && locationIds.length > 0) {
      const ids = locationIds.join(',');
      queryParams.push(`locationIds=${ids}`);
    }

    if (Array.isArray(contractIds) && contractIds.length > 0) {
      const ids = contractIds.join(',');
      queryParams.push(`contractIds=${ids}`);
    }

    if (Array.isArray(clientIds) && clientIds.length > 0) {
      const ids = clientIds.join(',');
      queryParams.push(`clientIds=${ids}`);
    }

    if (Array.isArray(sourceSystemIDs) && sourceSystemIDs.length > 0) {
      queryParams.push(`sourceSystemIds=${sourceSystemIDs.join(',')}`);
    }

    if (Array.isArray(projects) && projects.length > 0) {
      queryParams.push(
        `projects=${projects.map((x) => encodeURIComponent(x)).join(',')}`
      );
    }

    if (belongsTo) {
      queryParams.push(`belongsTo=${encodeURIComponent(belongsTo)}`);
    }

    if (sampleNumber) {
      if (divisionIds.includes(Divisions.Verifuel)) {
        queryParams.push(`clientSampleNo=${encodeURIComponent(sampleNumber)}`);
      } else {
        queryParams.push(`sourceSystemSampleID=${encodeURIComponent(sampleNumber)}`);
      }
    }

    if (gradeId) {
      queryParams.push(`gradeId=${gradeId}`);
    }

    if (page) {
      queryParams.push(`currentPage=${page}`);
    }

    if (pageSize) {
      queryParams.push(`pageSize=${pageSize}`);
    }

    if (sortBy) {
      queryParams.push(`sortBy=${sortBy}`);
    }

    const response = await axiosInstance.get(
      `/job/jobreports/all?${queryParams.join('&')}`
    );
    return response.data;
  } catch (error) {
    throw new Error('Error searchReports', error);
  }
};

const getJobReportContentByManyJobReportIDs = async ({ jobReportIds }) => {
  try {
    const response = await axiosInstance.post(
      '/report/GetReportContent/reportIDs',
      jobReportIds,
      {
        responseType: 'blob',
      }
    );

    return response.data;
  } catch (e) {
    throw new Error('Error getJobReportContentByManyJobReportIDs');
  }
};

const getReportContentByJobReportID = async ({ jobReportId }) => {
  try {
    const response = await axiosInstance.get(
      `/job/reportcontent/id/${jobReportId}`,
      {
        responseType: 'blob',
      }
    );

    const fileName = response.headers['content-disposition']
      .split('filename=')[1]
      .split(';')[0];

    return { fileName: fileName.replace(/"/g, ''), blobData: response.data };
  } catch (e) {
    throw new Error('Error getReportContentByJobReportID');
  }
};

const exportJobSearchResult = async ({
  jobName,
  project,
  jobReceivedDateFrom,
  jobReceivedDateTo,
  jobCompletedDateFrom,
  jobCompletedDateTo,
  clientName,
  jobStatusName,
  jobStatusId,
  jobProgress,
  locationID,
  locationIDs,
  clientIDs,
  sourceSystemIDs,
  projects,
  belongsTo,
  page,
  pageSize,
  sortBy,
}) => {
  try {
    const response = await axiosInstance.post(
      '/Report/Job/Export',
      {
        jobName,
        project,
        jobReceivedDateFrom,
        jobReceivedDateTo,
        jobCompletedDateFrom,
        jobCompletedDateTo,
        clientName,
        jobStatusName,
        jobStatusId,
        jobProgress,
        locationID,
        locationIDs,
        clientIDs,
        sourceSystemIDs,
        projects,
        belongsTo,
        page,
        pageSize,
        sortBy,
        FileType: 'CSV',
        Columns: [
          {
            ColumnName: 'JobIDText',
            Order: 2,
          },
          {
            ColumnName: 'JobName',
            Order: 1,
          },
        ],
      },
      {
        responseType: 'blob',
      }
    );

    return response.data;
  } catch (e) {
    console.log(e);
    throw new Error('Error Export Job Results');
  }
};

const exportReportResult = async ({
  ClientID,
  ProductGradeID,
  DateCreatedFrom,
  DateCreatedTo,
  Projects,
  ClientIDs,
  LocationIDs,
  SourceSystemIDs,
  DivisionIDs,
}) => {
  try {
    const response = await axiosInstance.post(
      '/Report/Result/Export',
      {
        ClientID,
        ProductGradeID,
        DateCreatedFrom,
        DateCreatedTo,
        Projects,
        ClientIDs,
        LocationIDs,
        SourceSystemIDs,
        DivisionIDs,
      },
      {
        responseType: 'blob',
      }
    );

    const fileName = response.headers['content-disposition']
      .split('filename=')[1]
      .split(';')[0];

    return { fileName: fileName.replace(/"/g, ''), blobData: response.data };
  } catch (e) {
    console.log(e);
    throw new Error('Error Export Result Results');
  }
};

const downloadTransactionOutput = async (transactionID) => {
  // console.log('Downloading transaction output for ID:', transactionID);
  const response = await axiosInstance.get(`Report/tranid/${transactionID}/download`, {
  // const response = await axiosInstance.get('https://morth.nic.in/sites/default/files/dd12-13_0.pdf', {
    responseType: 'blob'
    // responseType: 'text/plain'
  });
  // console.log('Received response for transaction output download:', response);
  const fileName = getFilenameFromHttpResponse(response);

  return { fileName, data: response.data };
};

export const getFilenameFromHttpResponseNew = (response) => {
  let filename = '';
  const disposition = response.headers.get('Content-Disposition');
  if (disposition && disposition.indexOf('attachment') !== -1) {
    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
    const matches = filenameRegex.exec(disposition);
    if (matches != null && matches[1]) {
      filename = matches[1].replace(/['"]/g, '');
    }
  }

  return filename;
};

export const downloadBlobFile = (filename, blob) => {
  // see: https://stackoverflow.com/questions/19327749/javascript-blob-filename-without-link

  // get downloadable url from the blob
  const blobUrl = URL.createObjectURL(blob);

  // create temp link element
  let link = document.createElement('a');
  link.download = filename;
  link.href = blobUrl;

  // use the link to invoke a download
  document.body.appendChild(link);
  link.click();

  // remove the link
  setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    link = null;
  }, 0);
};

const downloadTransactionOutputNew = async (transactionID) => {
  const response = await axiosInstance.get(`Report/tranid/${transactionID}/download`, {
    responseType: 'blob'
  });
  // console.log('response', response);
  const fileName = getFilenameFromHttpResponseNew(response);

  downloadBlobFile(fileName, response?.data);

  // return { fileName, data: response.data };
};

const downloadSampleReportPetrohub = async (documentPath) => {
  const response = await axiosInstance.get(`/Report/Petrohub/DownloadReport/${documentPath}`, {
    responseType: 'blob'
  });
  // console.log('response', response);
  const fileName = getFilenameFromHttpResponseNew(response);

  downloadBlobFile(fileName, response?.data);

  // return { fileName, data: response.data };
};

export const generateExportReportAsync = async (exportPayload) => {
  const response = postHandler(axiosInstance, 'Report/GenerateAsync', exportPayload);
  return response;
};
export const generateReportForPetrohub = async (exportPayload) => {
  // const response = postHandler(axiosInstance, 'Report/GenerateReportForPetrohub', exportPayload);
  const response = postHandler(axiosInstance, 'Report/GenerateAsync', exportPayload);
  return response;
};

const reportApi = {
  searchReports,
  getJobReportContentByManyJobReportIDs,
  exportJobSearchResult,
  exportReportResult,
  getReportContentByJobReportID,
  generateExportReportAsync,
  generateReportForPetrohub,
  downloadTransactionOutput,
  downloadTransactionOutputNew,
  downloadSampleReportPetrohub,
  downloadBlobFile,
  getFilenameFromHttpResponseNew,
};

export default reportApi;
